import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  FileText,
  Sparkles,
  ChevronRight,
  Star,
  MapPin,
  Mail,
  Phone,
  Award,
  Briefcase,
  TrendingUp,
  Target,
  Users,
  BadgeCheck,
  MessageCircle,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { HOME_SERVICE } from "../lib/services";
import { usePageMeta, ORG_JSON_LD } from "../lib/usePageMeta";
import Footer from "../components/Footer";
import VideoTestimonials from "../components/VideoTestimonials";
import GoogleReviews from "../components/GoogleReviews";
import CaseStudies from "../components/CaseStudies";
import TrustedBy from "../components/TrustedBy";
import HomeFAQ from "../components/HomeFAQ";
import { CONTACT } from "../lib/site-data";

const HERO_IMG_WEBP = "/img/hero.webp";
const HERO_IMG_JPG = "/img/hero.jpg";

export default function Home({ service = HOME_SERVICE }) {
  const navigate = useNavigate();
  // Attach Organization JSON-LD on top of whichever service (Home / Executive)
  // metadata the caller passed in — safe merge, no override.
  usePageMeta({ ...service, jsonLd: service.jsonLd || ORG_JSON_LD });
  const { hero } = service;

  return (
    <>
      <Navbar />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 rise-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand/10 text-brand text-xs font-semibold tracking-wide uppercase">
              <Star className="h-3.5 w-3.5 fill-current" /> {hero.eyebrow}
            </div>
            <h1 className="mt-6 font-serif font-bold leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
              {hero.headline[0]}{" "}
              <span className="relative inline-block whitespace-nowrap">
                <span className="relative z-10">{hero.headlineHighlight}</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-brand/30 -z-0 rounded-sm" />
              </span>
            </h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-xl leading-relaxed">
              {hero.sub}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-build-ai"
                onClick={() => navigate("/ai-cv-builder")}
                className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold"
              >
                <Sparkles className="h-4 w-4" /> Build My CV with AI — £9.99
              </button>
              <button
                data-testid="hero-hire-writer"
                onClick={() => navigate("/services/cv-writing")}
                className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold border border-foreground bg-foreground text-background hover:bg-foreground/90 transition"
              >
                Hire a Professional CV Writer — From £50 <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-foreground/70">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-brand fill-current" />
                4.8★ Google Rating
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-brand" />
                ATS-Friendly
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-brand" />
                UK Career Experts
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand" />
                60-Day Interview Guarantee
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative rise-in">
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-border shadow-2xl bg-muted">
              <picture>
                <source srcSet={HERO_IMG_WEBP} type="image/webp" />
                <img
                  src={HERO_IMG_JPG}
                  alt="UK CV writers at work"
                  fetchpriority="high"
                  decoding="async"
                  width="1000"
                  height="1250"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 text-white">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] opacity-80">
                    Trusted by
                  </p>
                  <p className="font-serif text-2xl font-semibold">
                    1,200+ UK professionals
                  </p>
                </div>
                <div className="bg-white text-foreground rounded-2xl px-4 py-2 text-xs font-semibold">
                  ★ 4.8 / 5
                </div>
              </div>
            </div>

            {/* Floating sticker */}
            <div
              data-testid="hero-guarantee-sticker"
              className="absolute -left-6 -bottom-8 md:left-auto md:-right-6 bg-foreground text-background rounded-2xl px-5 py-4 shadow-xl rotate-[-4deg] hidden sm:block"
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-brand-300">
                Promise
              </p>
              <p className="font-serif text-lg font-semibold leading-tight">
                60-Day
                <br /> Interview Guarantee
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section
        data-testid="trust-bar"
        className="border-y border-border bg-muted/30"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-6 text-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-brand">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <p className="font-semibold">4.8/5 on Google</p>
          </div>
          <p className="text-foreground/70 hidden md:block">
            AI CV Builder · Professional UK CV Writers · ATS-friendly
          </p>
          <p className="text-foreground/70 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand" />
            60-Day Interview Guarantee
          </p>
        </div>
      </section>

      <TrustedBy />

      {/* TWO WAYS TO BUILD YOUR CV */}
      <section
        id="two-ways"
        data-testid="home-two-ways"
        className="py-20 md:py-28 bg-background"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="label-eyebrow inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Two ways to build your CV
            </p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
              Choose the way that fits <span className="text-brand">where you are</span>.
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground/70">
              Both options are built on the same professional CV writing methodology used
              by our UK career experts — proven with recruiters, and tuned for ATS.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <article
              data-testid="home-cta-ai"
              className="bg-card border border-border rounded-3xl p-8 md:p-10 flex flex-col relative overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-brand/10 blur-3xl" />
              <div className="relative flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <div className="h-12 w-12 rounded-2xl bg-brand text-white flex items-center justify-center">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-brand bg-brand/10 rounded-full px-3 py-1">
                    Self-serve · Instant
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold mt-6">
                  AI CV Builder
                </h3>
                <p className="font-serif text-4xl font-bold mt-2">£9.99</p>
                <p className="text-xs text-muted-foreground mt-1">
                  One-off · no subscription
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-foreground/85 flex-1">
                  {[
                    "AI-guided CV creation, step-by-step, developed by Top CV experts",
                    "Premium templates (Classic, Modern, Compact)",
                    "ATS-friendly workflow — proper structure, industry keywords, right formatting etc.",
                    "AI-written cover letter alongside your CV",
                    "Unlimited edits from your dashboard",
                    "PDF & DOCX download",
                    "Sarah AI Career Consultant available throughout",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <BadgeCheck className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid="home-start-ai-cv"
                  onClick={() => navigate("/ai-cv-builder")}
                  className="btn-primary mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
                >
                  Build with AI <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </article>

            <article
              data-testid="home-cta-writer"
              className="bg-foreground text-background border border-foreground rounded-3xl p-8 md:p-10 flex flex-col relative overflow-hidden"
            >
              <div className="absolute -top-12 -left-12 h-40 w-40 rounded-full bg-brand/20 blur-3xl" />
              <div className="relative flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <div className="h-12 w-12 rounded-2xl bg-brand text-white flex items-center justify-center">
                    <Users className="h-6 w-6" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-brand bg-brand/15 rounded-full px-3 py-1">
                    Done-for-you
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-bold mt-6">
                  Professional CV Writing
                </h3>
                <p className="font-serif text-4xl font-bold mt-2">From £50</p>
                <p className="text-xs text-background/60 mt-1">
                  Standard CV → Premium Package
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-background/85 flex-1">
                  {[
                    "Experienced UK CV writers, matched to your industry",
                    "ATS optimisation baked into every draft",
                    "Fully tailored to your career story and target roles",
                    "Cover Letter & LinkedIn options available",
                    "Deep industry expertise — NHS, IT, Finance, Executive & more",
                    "60-Day Interview Guarantee",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <BadgeCheck className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid="home-view-packages"
                  onClick={() => navigate("/services/cv-writing")}
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-background text-foreground px-6 py-3.5 text-sm font-semibold hover:bg-background/90 transition"
                >
                  Hire a Professional Writer <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </article>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Not sure which is right for you? Chat with Sarah — your AI Career Consultant — bottom-right.
          </p>
        </div>
      </section>

      {/* WHY CV FOLKS IS DIFFERENT */}
      <section
        id="why-us"
        data-testid="why-different-section"
        className="py-24 md:py-32 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <p className="label-eyebrow">Why CV Folks is different</p>
              <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
                Professional Expertise.{" "}
                <span className="text-brand">AI Speed.</span>
              </h2>
              <p className="mt-5 text-foreground/70 leading-relaxed">
                CV Folks isn't just another AI CV builder, and it isn't just another
                CV writing agency. We combine the two — the workflow, care and
                methodology of professional UK CV writers, delivered at the speed
                and price of AI.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-border bg-card p-4 pr-6">
                <span className="h-10 w-10 rounded-xl bg-brand text-white flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div className="text-left">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    One connected platform
                  </p>
                  <p className="font-serif text-lg font-bold leading-tight">
                    From £9.99 AI CV to Premium Writer
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Sparkles, title: "Advanced AI Technology", desc: "Creates ATS-friendly, recruiter-focused CVs with achievement-driven content in minutes." },
                { icon: Users, title: "Professional CV writing expertise", desc: "Experienced CV writers behind every methodology — the same craft on both routes." },
                { icon: Target, title: "Recruiter-informed best practices", desc: "Trained on the guidance recruiters actually apply, not generic templates." },
                { icon: FileText, title: "ATS optimisation", desc: "Every draft — AI or human — is designed to pass applicant tracking systems." },
                { icon: BadgeCheck, title: "Premium templates", desc: "Classic, Modern and Compact designs built for UK hiring standards." },
                { icon: ShieldCheck, title: "Human career support", desc: "Real writers step in the moment your career needs a human touch." },
              ].map((b, i) => (
                <div
                  key={i}
                  data-testid={`why-diff-item-${i}`}
                  className="rounded-2xl border border-border p-6 bg-card hover:border-foreground/40 hover:shadow-lg transition"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <b.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-lg font-semibold leading-snug">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground/70 leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MEET SARAH */}
      <section
        data-testid="meet-sarah-section"
        className="py-24 md:py-32 bg-background"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="relative aspect-square rounded-[2rem] overflow-hidden border border-border bg-muted shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2.8&w=800&h=800&q=80"
                alt="Sarah — AI Career Consultant at CV Folks"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <div>
                  <p className="text-xs font-semibold text-foreground leading-tight">
                    Sarah is online — reply in seconds
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    AI Career Consultant · CV Folks
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="label-eyebrow">Meet Sarah</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
              Your <span className="text-brand">AI Career Consultant</span>, on tap.
            </h2>
            <p className="mt-5 text-foreground/70 leading-relaxed">
              Sarah isn't a scripted chatbot. She's an AI career consultant, trained on
              published UK CV bestsellers and CV Folks' own writing standards — the same
              principles our human writers use every day.
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-sm">
              {[
                "Helps you pick the right service (AI or Professional)",
                "Suggests improvements to your existing CV",
                "Explains how ATS actually works — no jargon",
                "Recommends the right package for your role",
                "Answers career questions in plain English",
                "Guides you through the whole platform",
              ].map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-xl border border-border bg-card p-3"
                >
                  <BadgeCheck className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-xs text-muted-foreground">
              Look for the chat bubble bottom-right — Sarah is standing by.
            </p>
            <div className="mt-6">
              <button
                type="button"
                data-testid="meet-sarah-chat-cta"
                onClick={() => window.dispatchEvent(new Event("sarah:open"))}
                className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
              >
                <MessageCircle className="h-4 w-4" /> Chat with Sarah
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* AI + HUMAN TOGETHER */}
      <section
        data-testid="ai-plus-human-section"
        className="relative py-24 md:py-32 bg-foreground text-background overflow-hidden"
      >
        <div className="absolute inset-0 grain opacity-40" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand/20 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-300 font-bold">
              One connected platform
            </p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-4 leading-[1.05]">
              You don't have to choose between{" "}
              <span className="text-brand">AI and expert writers.</span>
            </h2>
            <p className="mt-5 text-background/75 leading-relaxed text-base md:text-lg">
              Start with the AI CV Builder for £9.99. Love it? Ship it. Want a
              deeper craft? Upgrade to a professional UK writer at any time — and we'll
              credit your £9.99 back against the writing package.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              {
                step: "01",
                icon: Sparkles,
                title: "Start with AI — £9.99",
                desc: "Build a UK-English, ATS-friendly CV in minutes. Preview it before you pay.",
              },
              {
                step: "02",
                icon: FileText,
                title: "Keep it — or level up",
                desc: "Ship the AI CV, edit it forever, or hand it to a professional writer.",
              },
              {
                step: "03",
                icon: Users,
                title: "Upgrade with credit",
                desc: "Your £9.99 comes off any Professional Writing package — no wasted spend.",
              },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`ai-human-step-${i}`}
                className="bg-background/5 border border-background/10 rounded-2xl p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="h-10 w-10 rounded-xl bg-brand text-white flex items-center justify-center">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span className="font-serif text-3xl font-bold text-brand-300">
                    {s.step}
                  </span>
                </div>
                <p className="font-serif text-xl font-semibold mt-5">{s.title}</p>
                <p className="text-sm text-background/70 mt-2 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-12">
            <button
              type="button"
              data-testid="ai-human-start-ai"
              onClick={() => navigate("/ai-cv-builder")}
              className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
            >
              <Sparkles className="h-4 w-4" /> Start with AI — £9.99
            </button>
            <button
              type="button"
              data-testid="ai-human-view-packages"
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-2 rounded-full bg-background text-foreground px-6 py-3.5 text-sm font-semibold hover:bg-background/90 transition"
            >
              View Professional packages <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* GUARANTEE BANNER */}
      <section
        data-testid="guarantee-banner"
        className="relative bg-foreground text-background overflow-hidden"
      >
        <div className="absolute inset-0 grain" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-300 font-bold">
              Our Promise to You
            </p>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.05]">
              60-Day{" "}
              <span className="text-brand">Interview Guarantee</span>
            </h2>
            <p className="mt-6 text-background/75 max-w-2xl leading-relaxed text-lg">
              Your success matters. If you don't secure interviews within 60
              days of active job applications, we'll provide additional expert
              support and CV optimisation at no extra cost.
            </p>
          </div>

          <div className="md:col-span-5 grid grid-cols-2 gap-4">
            {[
              { label: "Avg. interviews in 60 days", value: "5+" },
              { label: "Years of UK experience", value: "10+" },
              { label: "Roles & industries", value: "120+" },
              { label: "5-star reviews", value: "150+" },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`stat-${i}`}
                className="bg-background/5 border border-background/10 rounded-2xl p-6"
              >
                <p className="font-serif text-4xl font-bold text-brand-300">
                  {s.value}
                </p>
                <p className="text-xs mt-2 text-background/60 uppercase tracking-widest">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <VideoTestimonials />

      <GoogleReviews />

      <CaseStudies />

      <HomeFAQ />

      {/* CONTACT */}
      <section
        id="contact"
        data-testid="contact-section"
        className="py-24 md:py-32 bg-muted/30"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="label-eyebrow">Get in touch</p>
          <h2 className="mt-3 font-serif text-3xl md:text-5xl font-bold leading-tight">
            We're here when you need CV help.
          </h2>
          <p className="mt-5 text-foreground/70 leading-relaxed max-w-xl mx-auto">
            Need CV help or a quick second opinion before you buy? WhatsApp
            our team — you'll get a reply from a real CV writer, usually
            within minutes during UK working hours.
          </p>

          <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
            <div className="rounded-2xl border border-border bg-card p-6">
              <span className="h-10 w-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                <Mail className="h-4 w-4" />
              </span>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-4">
                Email
              </p>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="font-semibold hover:text-brand block mt-1 break-all"
              >
                {CONTACT.email}
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <span className="h-10 w-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                <Phone className="h-4 w-4" />
              </span>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-4">
                WhatsApp (only)
              </p>
              <a
                href={`https://wa.me/${CONTACT.whatsappRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="font-semibold hover:text-brand block mt-1"
              >
                {CONTACT.whatsapp}
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <span className="h-10 w-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                <MapPin className="h-4 w-4" />
              </span>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-4">
                Visit us
              </p>
              <p className="font-semibold mt-1">{CONTACT.address}</p>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-3 justify-center">
            <button
              data-testid="contact-cta-ai"
              onClick={() => navigate("/ai-cv-builder")}
              className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold"
            >
              <Sparkles className="h-4 w-4" /> Build My CV with AI — £9.99
            </button>
            <button
              data-testid="contact-cta-writer"
              onClick={() => navigate("/services/cv-writing")}
              className="inline-flex items-center gap-2 rounded-full border border-foreground bg-foreground text-background hover:bg-foreground/90 px-7 py-4 text-sm font-semibold transition"
            >
              Hire a Professional CV Writer — From £50 <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
